/**
 * 계정 메일 링크에서 열어야 하는 화면과 일회용 토큰을 판정합니다.
 *
 * 보안 원칙:
 * - 토큰은 화면 메모리와 중앙 confirm/cancel 요청에만 사용합니다.
 * - console, 서버 로그, localStorage, sessionStorage에 기록하지 않습니다.
 * - 루트 주소의 일반 `token`은 action 값이 정확히 일치할 때만 계정 토큰으로 봅니다.
 */
export type AccountMailAction = 'password-reset' | 'recovery-email-verify' | null

export interface AccountMailLocation {
  action: AccountMailAction
  token: string
  cancelToken: string
  cancelUserId: number
  recoveryEmailUserId: number
  passwordResetMode: 'resetConfirm' | 'resetCancel' | null
  openAccountView: boolean
}

/** 기존 AuthPanel과 단위시험 호환을 위한 비밀번호 복구 전용 결과입니다. */
export interface PasswordRecoveryLocation {
  standardResetPath: boolean
  legacyResetAction: boolean
  resetToken: string
  cancelToken: string
  cancelUserId: number
  mode: 'resetConfirm' | 'resetCancel' | null
  openAccountView: boolean
}

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/'
  return pathname.replace(/\/+$/, '') || '/'
}

function normalizedAction(query: URLSearchParams): string {
  return query.get('action')?.trim().toLowerCase() ?? ''
}

/**
 * 표준 및 현재 중앙 메일 링크를 하나의 안전한 계정 라우트로 해석합니다.
 *
 * 비밀번호 재설정:
 * - `/account/password-reset?token=...`
 * - `/?action=password-reset&passwordResetToken=...`
 * - 과거 `/?resetToken=...`
 *
 * 복구 이메일 인증:
 * - `/account/recovery-email-verify?userId=...&token=...` (2n4uMember 표준)
 * - `/account/recovery-email/verify?token=...` (기존 mioSegno 호환)
 * - `/?action=recovery-email-verify&emailVerificationToken=...`
 * - `/?action=verify-recovery-email&token=...`
 */
export function parseAccountMailLocation(
  pathname: string,
  search: string,
): AccountMailLocation {
  const path = normalizePathname(pathname)
  const query = new URLSearchParams(search)
  const action = normalizedAction(query)

  const standardPasswordReset = path === '/account/password-reset'
  const legacyPasswordReset = path === '/' && action === 'password-reset'
  const standardRecoveryEmailVerify = path === '/account/recovery-email-verify'
    || path === '/account/recovery-email/verify'
  const legacyRecoveryEmailVerify = path === '/'
    && ['recovery-email-verify', 'verify-recovery-email', 'recovery-email-verification']
      .includes(action)

  let resolvedAction: AccountMailAction = null
  let token = ''

  if (standardPasswordReset || legacyPasswordReset) {
    resolvedAction = 'password-reset'
    token = standardPasswordReset
      ? query.get('token')?.trim() ?? ''
      : query.get('passwordResetToken')?.trim()
        || query.get('token')?.trim()
        || ''
  } else if (standardRecoveryEmailVerify || legacyRecoveryEmailVerify) {
    resolvedAction = 'recovery-email-verify'
    token = standardRecoveryEmailVerify
      ? query.get('token')?.trim() ?? ''
      : query.get('emailVerificationToken')?.trim()
        || query.get('recoveryEmailVerificationToken')?.trim()
        || query.get('token')?.trim()
        || ''
  } else if (query.get('resetToken')?.trim()) {
    resolvedAction = 'password-reset'
    token = query.get('resetToken')?.trim() ?? ''
  }

  const cancelToken = query.get('cancelResetToken')?.trim() ?? ''
  const rawCancelUserId = Number(query.get('resetUserId') ?? '0')
  const cancelUserId = Number.isSafeInteger(rawCancelUserId) && rawCancelUserId > 0
    ? rawCancelUserId
    : 0


  const rawRecoveryEmailUserId = Number(
    query.get('userId')
      ?? query.get('recoveryEmailUserId')
      ?? query.get('verificationUserId')
      ?? '0',
  )
  const recoveryEmailUserId = Number.isSafeInteger(rawRecoveryEmailUserId)
    && rawRecoveryEmailUserId > 0
    ? rawRecoveryEmailUserId
    : 0

  const passwordResetMode = resolvedAction === 'password-reset' && token
    ? 'resetConfirm'
    : cancelToken && cancelUserId > 0
      ? 'resetCancel'
      : null

  return {
    action: resolvedAction,
    token,
    cancelToken,
    cancelUserId,
    recoveryEmailUserId,
    passwordResetMode,
    // action이 명시됐는데 토큰이 빠진 잘못된 링크도 홈으로 보내지 않고
    // 계정 화면에서 사용자에게 정확한 오류를 보여줍니다.
    openAccountView:
      standardPasswordReset
      || legacyPasswordReset
      || standardRecoveryEmailVerify
      || legacyRecoveryEmailVerify
      || passwordResetMode !== null,
  }
}

export function parsePasswordRecoveryLocation(
  pathname: string,
  search: string,
): PasswordRecoveryLocation {
  const path = normalizePathname(pathname)
  const query = new URLSearchParams(search)
  const parsed = parseAccountMailLocation(pathname, search)
  const standardResetPath = path === '/account/password-reset'
  const legacyResetAction = path === '/' && normalizedAction(query) === 'password-reset'

  return {
    standardResetPath,
    legacyResetAction,
    resetToken: parsed.action === 'password-reset' ? parsed.token : '',
    cancelToken: parsed.cancelToken,
    cancelUserId: parsed.cancelUserId,
    mode: parsed.passwordResetMode,
    openAccountView:
      standardResetPath
      || legacyResetAction
      || parsed.passwordResetMode !== null,
  }
}

export function shouldOpenAccountViewForLocation(pathname: string, search: string): boolean {
  return parseAccountMailLocation(pathname, search).openAccountView
}
